export const useRevisionTecnica = () => {
  const config = useRuntimeConfig();

  const debug = {
    enabled: config.public.modo === 'DEV',
    log(...args) {
      if (this.enabled) console.log('[useRevisionTecnica]', ...args);
    },
    error(...args) {
      if (this.enabled) console.error('[useRevisionTecnica]', ...args);
    },
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email && emailRegex.test(email);
  };

  const validatePhoneNumber = (phone, country = '+56') => {
    const value = String(phone || '').replace(/\D/g, '');
    if (country === '+56') return value.length === 9;
    return value.length >= 8 && value.length <= 12;
  };

  const validateString = (value) => {
    const regex = /^[a-zA-ZÀ-ÖØ-öø-ÿ\s]+$/;
    if (!regex.test(value)) {
      return value.replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ\s]+/g, '');
    }
    return value;
  };

  const formatearPrecio = (precio) => {
    const numero = Number(precio || 0);
    return new Intl.NumberFormat('es-CL').format(numero);
  };

  const formatearModalidad = (modo) => {
    if (Number(modo) === 1) return 'Espera Vehículo';
    if (Number(modo) === 2) return 'Deja Vehículo';
    if (Number(modo) === 3) return 'Retira Vehículo';
    return 'Modalidad no definida';
  };

  const transformarModalidad = (modo) => {
    if (Number(modo) === 2) return 'DEJA';
    if (Number(modo) === 3) return 'RETIRA';
    return 'ESPERA';
  };

  const formatearFecha = (fecha, hora) => {
    if (!fecha || !hora) return '';

    const fechaObj = new Date(`${fecha}T00:00:00`);
    const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

    return `${dias[fechaObj.getDay()]}, ${fechaObj.getDate()} de ${meses[fechaObj.getMonth()]} de ${fechaObj.getFullYear()}. Hora ${hora}`;
  };

  const validarDatosVehiculo = ({ marca, ano, modelo, local }) => {
    return !!(marca && ano && modelo && local);
  };

  const validarDatosPatente = ({ patente, local }) => {
    const valor = String(patente || '').replace(/[^A-Za-z0-9]/g, '').trim();
    return !!(valor.length >= 6 && valor.length <= 8 && local);
  };

  const validarBusqueda = (searchData) => {
    const { local, modo_busqueda, patente, marca, ano, modelo } = searchData || {};
    if (!local) return false;

    if (modo_busqueda === 'por_patente') {
      return validarDatosPatente({ patente, local });
    }

    return validarDatosVehiculo({ marca, ano, modelo, local });
  };

  const validarDatosPersonales = (form) => {
    if (!form?.nombre) return false;
    if (!form?.apellido_paterno) return false;
    if (!form?.email || !validateEmail(form.email)) return false;
    if (!validatePhoneNumber(form.telefono, form?.pais || '+56')) return false;
    return true;
  };

  const cargarDetalleAgendamiento = async (data) => {
    const response = await $fetch('/api/revisiontecnica/detalle', {
      method: 'POST',
      query: { action: 'getDetalleAgendamiento' },
      body: data,
    });

    if (response.status === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'No fue posible cargar el detalle del agendamiento.');
  };

  const cargarCalendario = async (data) => {
    const response = await $fetch('/api/revisiontecnica/calendario', {
      method: 'POST',
      query: { action: 'calendario' },
      body: data,
    });

    if (response.status === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'No fue posible cargar el calendario.');
  };

  const esOk = (response) => {
    const status = String(response?.status ?? '').toLowerCase();
    return status === 'success' || response?.success === true;
  };

  const extraerLista = (response) => {
    const lista = response?.data?.Lista ?? response?.data?.lista ?? response?.Lista ?? response?.lista ?? response?.data;
    return Array.isArray(lista) ? lista : null;
  };

  const buscarMarcas = async () => {
    try {
      const response = await $fetch('/api/revisiontecnica/vehiculos', { query: { action: 'marcas' } });
      const lista = extraerLista(response);
      if (esOk(response) && lista) return lista;
      return [];
    } catch (error) {
      debug.error('buscarMarcas', error);
      return [];
    }
  };

  const buscarAnosPorMarca = async (marca) => {
    try {
      const response = await $fetch('/api/revisiontecnica/vehiculos', {
        query: { action: 'anos', marca },
      });
      const lista = extraerLista(response);
      if (esOk(response) && lista) return lista;
      return [];
    } catch (error) {
      debug.error('buscarAnosPorMarca', error);
      return [];
    }
  };

  const buscarModelosPorMarcaAno = async (marca, ano) => {
    try {
      const response = await $fetch('/api/revisiontecnica/vehiculos', {
        query: { action: 'modelos', marca, ano },
      });
      const lista = extraerLista(response);
      if (esOk(response) && lista) return lista;
      return [];
    } catch (error) {
      debug.error('buscarModelosPorMarcaAno', error);
      return [];
    }
  };

  const buscarPorPatente = async (patente, local, local_id = null) => {
    const response = await $fetch('/api/revisiontecnica/vehiculos', {
      query: {
        action: 'patente',
        patente,
        local,
        local_id,
      },
    });

    if (response.status === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'No fue posible buscar el vehículo por patente.');
  };

  const construirPayloadAgendamiento = ({ searchData, detalle_agendamiento, personalInformation }) => {
    const fecha = detalle_agendamiento?.fecha || '';
    const hora = detalle_agendamiento?.hora || '';

    return {
      negocio: 'REVISION TECNICA',
      modo_busqueda: searchData?.modo_busqueda || 'por_patente',
      local: searchData?.local || '',
      local_id: searchData?.local_id || detalle_agendamiento?.local_info?.id || null,

      patente: (searchData?.patente || personalInformation?.patente || '').toUpperCase(),
      marca: (searchData?.marca || detalle_agendamiento?.vehiculo?.marca || '').toUpperCase(),
      ano: searchData?.ano || detalle_agendamiento?.vehiculo?.ano || '',
      modelo: (searchData?.modelo || detalle_agendamiento?.vehiculo?.modelo || '').toUpperCase(),

      nombre: validateString(personalInformation?.nombre || '').trim(),
      apellido_paterno: validateString(personalInformation?.apellido_paterno || '').trim(),
      apellido_materno: validateString(personalInformation?.apellido_materno || '').trim(),
      rut: personalInformation?.rut || '',
      email: (personalInformation?.email || '').trim().toLowerCase(),
      celular: `${String(personalInformation?.pais || '+56').replace(/\D/g, '')}${String(personalInformation?.telefono || '').replace(/\D/g, '')}`,

      modalidad: transformarModalidad(detalle_agendamiento?.modalidad),
      fecha_hora: fecha && hora ? `${fecha} ${hora}:00` : '',

      retiren_region: detalle_agendamiento?.direccion_retiro?.region || '',
      retiren_comuna: detalle_agendamiento?.direccion_retiro?.comuna || '',
      retiren_direccion: detalle_agendamiento?.direccion_retiro?.direccion || '',
      retiren_comentario: detalle_agendamiento?.direccion_retiro?.comentario || '',

      retiren_region_1: detalle_agendamiento?.direccion_entrega?.region || '',
      retiren_comuna_1: detalle_agendamiento?.direccion_entrega?.comuna || '',
      retiren_direccion_1: detalle_agendamiento?.direccion_entrega?.direccion || '',
      retiren_comentario_1: detalle_agendamiento?.direccion_entrega?.comentario || '',
    };
  };

  const normalizarMensajeAgendamiento = (mensaje) => {
    const texto = String(mensaje || '').toLowerCase()

    if (
      texto.includes('terminated') ||
      texto.includes('timeout') ||
      texto.includes('fetch failed') ||
      texto.includes('abort')
    ) {
      return 'El servidor demoró demasiado en responder. Intenta nuevamente en unos momentos.'
    }

    return String(mensaje || 'No fue posible registrar el agendamiento.')
  }

  const agendar = async (payload) => {
    try {
      const response = await $fetch('/api/revisiontecnica/agendar', {
        method: 'POST',
        body: {
          action: 'agendar',
          payload,
        },
      })

      if (response.status === 'success' || response?.data?.status === 'success') {
        return response.data || response
      }

      throw new Error(normalizarMensajeAgendamiento(response?.message))
    } catch (error) {
      debug.error('agendar', error)
      throw new Error(
        normalizarMensajeAgendamiento(
          error?.data?.message || error?.message
        )
      )
    }
  }

  return {
    validateEmail,
    validatePhoneNumber,
    validateString,
    formatearPrecio,
    formatearFecha,
    formatearModalidad,
    transformarModalidad,
    validarDatosVehiculo,
    validarDatosPatente,
    validarBusqueda,
    validarDatosPersonales,
    cargarDetalleAgendamiento,
    cargarCalendario,
    buscarMarcas,
    buscarAnosPorMarca,
    buscarModelosPorMarcaAno,
    buscarPorPatente,
    construirPayloadAgendamiento,
    agendar,
  };
};
